import { isEmpty, pickBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import classnames from 'classnames';

// Components:
import { StyledStar } from 'src/components/Filters/FilterStars';
import SuggestedFilters from 'src/routes/SearchPage/components/Filters/components/FiltersPanel/SuggestedFilters';
import styles from './filterContainer.module.css';
import Container from './components/Container';
import { FILTERS_LIST } from './components/Filters';
import { FiltersClear } from './components/FiltersHelpers/FiltersClear';
import { FiltersApplyMobile } from './components/mobile';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';

export const FilterContainer = (props) => {
  const {
    filters,
    touristsCount,
    hasAwaitNewResults,
    sidebarIsHidden,
    changeFilter,
    clearFilter,
    disabled,
    modifierStyles,
    currencyCode,
  } = props;

  const isMobile = useContext(MobileContext);
  const hasFilters = !isEmpty(filters);

  const [rebuildCount, setRebuildCount] = useState(0);
  const containerRef = useRef(null);

  const applySuggestedFilters = useCallback(() => {
    setRebuildCount((currentCount) => currentCount + 1);
  }, []);

  useEffect(() => {
    if (hasAwaitNewResults === false) {
      applySuggestedFilters();
    }
  }, [applySuggestedFilters, hasAwaitNewResults]);

  if (!hasFilters) return null;

  const getFilterData = (Filter) => {
    const { filterName, customFilters } = Filter;
    return filterName && customFilters
      ? pickBy(filters, (val, key) => val && customFilters.indexOf(key) !== -1)
      : filters[filterName];
  };

  const getFilters = () =>
    FILTERS_LIST.map((Filter) => {
      const data = Filter ? getFilterData(Filter) : null;

      if (isEmpty(data)) {
        return null;
      }
      const { filterName } = Filter || {};

      const StyledFilter = styled(Filter)`
        margin-bottom: 22px;
        line-heights: 20px;

        ${isMobile &&
        `.filter-rating {
            padding: 0 10px;
          }`}
      `;

      return Filter ? (
        <StyledFilter
          // Forcing to remount filters after previous set of filters applied
          key={`filter-${filterName}-${rebuildCount}`}
          onChange={changeFilter}
          className="filters__filter"
          data={data}
          onClear={clearFilter}
          touristsCount={touristsCount}
          isMobile={isMobile}
          currencyCode={currencyCode}
        />
      ) : null;
    });

  return (
    <div
      className={classnames(styles.container, {
        [styles.containerMobile]: isMobile,
        [styles.containerDisabled]: disabled,
      })}
    >
      <StyledWrapper
        isMobile={isMobile}
        ref={containerRef}
        boldTextColor={modifierStyles.boldTextColor}
        textColor={modifierStyles.textColor}
        highlitedBlockColor={modifierStyles.highlitedBlockColor}
      >
        <Container
          isMobile={isMobile}
          isOpen={!sidebarIsHidden}
          containerRef={containerRef}
        >
          <SuggestedFilters
            className={styles.suggestedFilters}
            isMobile={isMobile}
          />
          <div style={{ paddingBottom: isMobile ? 60 : 0 }}>{getFilters()}</div>
          {isMobile ? (
            <FiltersApplyMobile />
          ) : (
            <FiltersClear className={styles.filtersClear} />
          )}
        </Container>
      </StyledWrapper>
    </div>
  );
};

const StyledWrapper = styled.div`
  top: 0;
  width: 220px;
  min-width: 220px;

  ${({ boldTextColor }) => `color: ${boldTextColor};`}

  ${({ textColor }) =>
    textColor &&
    `
    input::placeholder { color: ${textColor}; };

    label div {
      color: ${textColor} !important;
    }

    label span {
      color: ${textColor} !important;
    }

    ${StyledStar} {
      stroke: ${textColor} !important;
    }

    input {
      color: ${textColor} !important;
    }
  `}

  ${({ highlitedBlockColor, textColor }) =>
    highlitedBlockColor &&
    textColor &&
    `
    label input[type="checkbox"] {
      &:checked {
        &:after {
          background-color: ${highlitedBlockColor} !important;
          background-image: none !important;
        }
      }
    }

    label input[type="radio"] {
      &:checked {
        &:before {
          background-color: ${highlitedBlockColor} !important;
          background-image: none !important;
        }
      }
    }

    button:not(.lt-switcher-item__active) {
      color: ${textColor};
    }

    button.lt-switcher-item__active {
      background-color: ${highlitedBlockColor} !important;
      background-image: none !important;

      svg {
        use {
          fill: ${highlitedBlockColor} !important;
        }
      }
    }

    .filter-regions {
      button {
        background-image: none !important;
        border-color: unset;
      }
    }
  `}
`;
